// Сессия через httpOnly-cookie.
//
// Токен сессии больше не хранится в localStorage (защита от кражи через XSS):
// сервер ставит httpOnly-cookie, а браузер прикладывает её только к запросам
// с credentials. Этот модуль:
//   1) включает отправку cookie (axios withCredentials / fetch credentials: 'include')
//      для всех запросов к нашему API;
//   2) при ответе 401 (сессия истекла, аккаунт удалён) очищает маркеры входа
//      и ведёт на /login.
//
// Подключается один раз в src/index.js до рендера приложения.

import axios from 'axios';
import config from '../config';

const LOGIN_PATH = '/login';
// Страницы, с которых не нужно перенаправлять на вход при 401
const PUBLIC_PATHS = ['/', '/login', '/registration', '/public-offer'];

// НЕ секретные маркеры «пользователь входил» (токена среди них нет)
export const SESSION_MARKER_KEYS = ['userId', 'isAdmin', 'role'];

export const clearSessionMarkers = () => {
  try {
    SESSION_MARKER_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    // localStorage может быть недоступен — просто продолжаем
  }
};

const hasSessionMarker = () => {
  try {
    return Boolean(localStorage.getItem('userId'));
  } catch (e) {
    return false;
  }
};

// Запрос к нашему API? (абсолютный адрес apiUrl или относительный /api/...)
export const isApiUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  if (config.apiUrl && url.startsWith(config.apiUrl)) return true;
  return url.startsWith('/api/');
};

// Эти эндпоинты сами отвечают 401 — это не означает «сессия истекла»
const isAuthEndpoint = (url = '') => /\/api\/auth\/(login|registration|logout|session)\b/.test(url);

let redirecting = false;
const handleUnauthorized = (url) => {
  if (!hasSessionMarker() || isAuthEndpoint(url) || redirecting) return;

  clearSessionMarkers();

  if (!PUBLIC_PATHS.includes(window.location.pathname)) {
    redirecting = true;
    window.location.assign(LOGIN_PATH);
  }
};

// ---------- axios ----------
axios.interceptors.request.use((request) => {
  if (isApiUrl(request.url)) {
    request.withCredentials = true;
  }
  return request;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && isApiUrl(error.config?.url)) {
      handleUnauthorized(error.config.url);
    }
    return Promise.reject(error);
  }
);

// ---------- fetch ----------
if (typeof window !== 'undefined' && typeof window.fetch === 'function' && !window.fetch.__apiAuthPatched) {
  const originalFetch = window.fetch.bind(window);

  const patchedFetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input instanceof URL ? input.href : null);
    let nextInit = init;

    if (url && isApiUrl(url) && !(init && init.credentials)) {
      nextInit = { ...init, credentials: 'include' };
    }

    const response = await originalFetch(input, nextInit);
    if (url && response.status === 401 && isApiUrl(url)) {
      handleUnauthorized(url);
    }
    return response;
  };

  patchedFetch.__apiAuthPatched = true;
  window.fetch = patchedFetch;
}
